import type { UseFormReturn } from "react-hook-form";
import type { DemandFormData } from "./DemandIntakeForm";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { AlertCircle, DollarSign } from "lucide-react";

interface FinancialInfoStepProps {
  form: UseFormReturn<DemandFormData>;
}

export function FinancialInfoStep({ form }: FinancialInfoStepProps) {
  const { register, formState: { errors }, setValue, watch } = form;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Estimated Cost */}
        <div className="space-y-2">
          <Label htmlFor="estimatedCost">
            Estimated Cost (USD) <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="estimatedCost"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              className={`pl-10 ${errors.estimatedCost ? "border-destructive" : ""}`}
              {...register("estimatedCost", {
                required: "Estimated cost is required",
                min: {
                  value: 0,
                  message: "Cost must be a positive number"
                }
              })}
            />
          </div>
          {errors.estimatedCost && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" />
              {errors.estimatedCost.message}
            </div>
          )}
        </div>

        {/* Budget Category */}
        <div className="space-y-2">
          <Label htmlFor="budgetCategory">
            Budget Category <span className="text-destructive">*</span>
          </Label>
          <Select
            onValueChange={(value) => setValue("budgetCategory", value)}
            value={watch("budgetCategory")}
          >
            <SelectTrigger className={errors.budgetCategory ? "border-destructive" : ""}>
              <SelectValue placeholder="Select budget category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="CAPEX">Capital Expenditure (CAPEX)</SelectItem>
              <SelectItem value="OPEX">Operating Expenditure (OPEX)</SelectItem>
              <SelectItem value="R&D">Research & Development</SelectItem>
              <SelectItem value="Marketing">Marketing & Sales</SelectItem>
              <SelectItem value="IT">Information Technology</SelectItem>
              <SelectItem value="HR">Human Resources</SelectItem>
              <SelectItem value="Facilities">Facilities & Infrastructure</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
          {errors.budgetCategory && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" />
              Budget category is required
            </div>
          )}
        </div>
      </div>

      {/* Cost Justification */}
      <div className="space-y-2">
        <Label htmlFor="costJustification">
          Cost Justification <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="costJustification"
          placeholder="Explain why this investment is necessary and how the cost is justified..."
          rows={4}
          {...register("costJustification", {
            required: "Cost justification is required",
            minLength: {
              value: 25,
              message: "Cost justification must be at least 25 characters"
            }
          })}
          className={errors.costJustification ? "border-destructive" : ""}
        />
        {errors.costJustification && (
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4" />
            {errors.costJustification.message}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Expected ROI */}
        <div className="space-y-2">
          <Label htmlFor="expectedROI">
            Expected ROI (%) <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <Input
              id="expectedROI"
              type="number"
              step="0.1"
              min="0"
              max="1000"
              placeholder="0.0"
              className={`pr-8 ${errors.expectedROI ? "border-destructive" : ""}`}
              {...register("expectedROI", {
                required: "Expected ROI is required",
                min: {
                  value: 0,
                  message: "ROI must be a positive number"
                }
              })}
            />
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">%</span>
          </div>
          {errors.expectedROI && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" />
              {errors.expectedROI.message}
            </div>
          )}
        </div>

        {/* Payback Period */}
        <div className="space-y-2">
          <Label htmlFor="paybackPeriod">
            Payback Period <span className="text-destructive">*</span>
          </Label>
          <Select
            onValueChange={(value) => setValue("paybackPeriod", value)}
            value={watch("paybackPeriod")}
          >
            <SelectTrigger className={errors.paybackPeriod ? "border-destructive" : ""}>
              <SelectValue placeholder="Select payback period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3-months">3 months</SelectItem>
              <SelectItem value="6-months">6 months</SelectItem>
              <SelectItem value="1-year">1 year</SelectItem>
              <SelectItem value="18-months">18 months</SelectItem>
              <SelectItem value="2-years">2 years</SelectItem>
              <SelectItem value="3-years">3 years</SelectItem>
              <SelectItem value="5-years">5+ years</SelectItem>
              <SelectItem value="no-payback">No direct payback expected</SelectItem>
            </SelectContent>
          </Select>
          {errors.paybackPeriod && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" />
              Payback period is required
            </div>
          )}
        </div>
      </div>

      {/* Additional Financial Notes */}
      <div className="bg-muted/50 p-4 rounded-lg">
        <h4 className="font-medium mb-2">Financial Guidelines</h4>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>• Requests over $10,000 require additional approval steps</li>
          <li>• CAPEX requests must align with annual budget planning</li>
          <li>• Include all associated costs (implementation, training, maintenance)</li>
          <li>• ROI calculations should include both direct and indirect benefits</li>
        </ul>
      </div>
    </div>
  );
}